import React, { useState, useRef, useEffect, useCallback } from 'react';
import useAddressAutocomplete from '../hooks/useAddressAutocomplete';

const AddressAutocomplete = ({ 
  value, 
  onChange, 
  onAddressSelect,
  placeholder, 
  className = '',
  required = false,
  countryCode = ''
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const { suggestions, isLoading, isReady, fetchSuggestions, selectSuggestion, clearSuggestions } = 
    useAddressAutocomplete({ apiKey, countryCode });

  const handleInputChange = useCallback((e) => {
    const val = e.target.value;
    onChange(val);
    if (isReady) fetchSuggestions(val);
  }, [onChange, fetchSuggestions, isReady]);

  const handleSelect = useCallback(async (suggestion) => {
    try {
      const parsed = await selectSuggestion(suggestion);
      onChange(parsed.address1);
      if (onAddressSelect) onAddressSelect(parsed);
    } catch {
      onChange(suggestion.description || suggestion.mainText);
    }
    clearSuggestions();
    setHighlightIndex(-1);
  }, [selectSuggestion, onChange, onAddressSelect, clearSuggestions]);

  const handleKeyDown = useCallback((e) => {
    if (!suggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[highlightIndex]);
    } else if (e.key === 'Escape') {
      clearSuggestions();
    }
  }, [suggestions, highlightIndex, handleSelect, clearSuggestions]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        clearSuggestions();
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [clearSuggestions]);

  const showDropdown = isFocused && suggestions.length > 0;

  return (
    <div ref={containerRef} className="address-autocomplete-container">
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={className}
          required={required}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          role="combobox"
        />
        {isLoading && (
          <div style={{
            position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
            width: 16, height: 16, border: '2px solid #e4e4e7', borderTopColor: '#a1a1aa',
            borderRadius: '50%', animation: 'spin 0.8s linear infinite'
          }} />
        )}
      </div>
      {showDropdown && (
        <div className="address-predictions" role="listbox">
          {suggestions.map((s, i) => (
            <div
              key={s.placeId}
              className={`prediction-item ${i === highlightIndex ? 'prediction-item-highlighted' : ''}`}
              onClick={() => handleSelect(s)}
              onMouseEnter={() => setHighlightIndex(i)}
              role="option"
              aria-selected={i === highlightIndex}
            >
              <span style={{ fontWeight: 600, color: '#18181b' }}>{s.mainText}</span>
              {s.secondaryText && (
                <span style={{ color: '#71717a', marginLeft: 4, fontSize: '0.85em' }}>{s.secondaryText}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
